import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Popup from 'reactjs-popup'; // Pour les confirmations
import MultiDeviceSelector from './MultiDeviceSelector';

/**
 * Modal pour éditer un groupe VideoWall (nom) et gérer ses clients (supprimer).
 *
 * PROPS:
 * - isVisible (boolean): Si le modal doit être affiché.
 * - onClose (function): Fonction à appeler pour fermer le modal (met aussi à jour la liste parente).
 * - serverData (object): L'objet contenant { nom, pin_code_serveur, pin_code_clients }
 */
const ModalEditVideowall = ({ isVisible, onClose, serverData }) => {

    // État pour le champ de nom modifiable
    const [nom, setNom] = useState('');
    // État pour la liste des clients (transformée en tableau)
    const [clients, setClients] = useState([]);
    // État de chargement pour les actions
    const [isLoading, setIsLoading] = useState(false);
    const [isSelectorVisible, setIsSelectorVisible] = useState(false);

    let token = localStorage.getItem("token");
    const headers = {
        'x-access-token': token,
        'Content-Type': 'application/json'
    };

    // `useEffect` pour charger les données du serveur quand le modal s'ouvre
    useEffect(() => {
        if (serverData) {
            setNom(serverData.nom || '');

            // Transformer la chaîne "client1,client2" en tableau ['client1', 'client2']
            if (serverData.pin_code_clients) {
                setClients(serverData.pin_code_clients.split(','));
            } else {
                setClients([]); // Gérer le cas où il n'y a pas de clients
            }
        }
    }, [serverData]); // Se déclenche à chaque fois que serverData change

    // Gérer la fermeture de base du modal
    const handleClose = (e) => {
        if (e.target.id === "wrapper") onClose();
    };

    /**
     * Gère la sauvegarde du nouveau nom.
     * Vous devez créer ce point d'API !
     */
    const handleSaveName = () => {
        if (!nom || nom === serverData.nom) {
            alert("Le nom est inchangé ou vide.");
            return;
        }

        setIsLoading(true);
        // APPEL API (à créer) : Mettre à jour le 'nom' pour toutes les lignes
        // ayant ce 'pin_code_serveur'
        axios.put(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/update_videowall_name`,
            {
                pin_code_serveur: serverData.pin_code_serveur,
                nouveau_nom: nom
            },
            { headers }
        )
            .then(res => {
                console.log("Nom mis à jour:", res.data);
                alert("Nom mis à jour avec succès !");
                onClose(); // Ferme et rafraîchit la liste parente
            })
            .catch(err => {
                console.error("Erreur lors de la mise à jour du nom:", err);
                alert("Erreur lors de la mise à jour du nom.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    /**
     * Gère la suppression d'un client spécifique du groupe.
     * Vous devez créer ce point d'API !
     */
    const handleRemoveClient = (clientPin) => {
        setIsLoading(true);

        // APPEL API (à créer) : Supprimer la liaison spécifique
        // Nous envoyons les deux pins pour trouver la bonne ligne à supprimer
        axios.delete(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/delete_client_videowall_pincode`,
            {
                headers: headers,
                data: {
                    pin_code_serveur: serverData.pin_code_serveur,
                    pin_code_client: clientPin
                }
            }
        )
            .then(res => {
                console.log("Client supprimé:", res.data);
                // Mettre à jour l'état local pour que l'UI soit réactive
                setClients(prevClients => prevClients.filter(c => c !== clientPin));
                // Note: 'onClose()' n'est pas appelé ici, mais il le sera 
                // lorsque l'utilisateur fermera le modal, rafraîchissant la liste principale.
            })
            .catch(err => {
                console.error("Erreur lors de la suppression du client:", err);
                alert("Erreur lors de la suppression du client.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // 3. Nouvelle fonction pour ajouter les clients sélectionnés
    const handleAddClients = (selectedPins) => {
        if (!selectedPins || selectedPins.length === 0) return;

        setIsLoading(true);
        setIsSelectorVisible(false); // Fermer le sélecteur

        // Préparer les données comme dans ModalModifyTizenbo
        const dataVw = {
            nom: nom, // On utilise le nom actuel (potentiellement édité)
            pin_code_serveur: serverData.pin_code_serveur,
            pin_code_client: selectedPins,
            notes: "" // Notes par défaut
        };

        // Appel API pour ajouter les clients
        axios.post(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/add_clients_videowall`,
            { dataVw }, // Attention à bien envelopper dans { dataVw } comme attendu par le back
            { headers }
        )
            .then(res => {
                console.log("Clients ajoutés avec succès:", res.data);
                // Mettre à jour la liste locale pour voir les nouveaux clients immédiatement
                setClients(prevClients => [...prevClients, ...selectedPins]);
            })
            .catch(err => {
                console.error("Erreur lors de l'ajout des clients:", err);
                alert("Erreur lors de l'ajout des clients.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };


    if (!isVisible) return null;

    return (
        // Le modal (backdrop)
        <>
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            id="wrapper"
            onClick={handleClose}
        >
            {/* Le conteneur du modal */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto flex flex-col gap-4 p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-800">Gérer le groupe {serverData.pin_code_serveur}</h2>
                    <button className="text-slate-400 hover:text-slate-600 text-xl leading-none" onClick={onClose}>&times;</button>
                </div>

                <div>
                    <label htmlFor="group_name" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nom du groupe
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            id="group_name"
                            value={nom}
                            onChange={(e) => setNom(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                            placeholder="Nom du groupe"
                        />
                        <button
                            onClick={handleSaveName}
                            disabled={isLoading || nom === serverData.nom}
                            className="rounded-lg bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-600 disabled:bg-slate-300"
                        >
                            {isLoading ? '...' : 'Sauver'}
                        </button>
                    </div>
                </div>

                {/* Section 2: Gérer les clients */}
                <div>
                    <div className="mb-2 flex items-center justify-between">

                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Clients liés
                        </label>
                        <button
                            onClick={() => setIsSelectorVisible(true)}
                            disabled={isLoading}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:bg-slate-300"
                        >
                            + Ajouter Client
                        </button>
                    </div>
                    <div className="max-h-60 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
                        {clients.length > 0 ? (
                            <ul className="divide-y divide-slate-200">
                                {clients.map((clientPin) => (
                                    <li key={clientPin} className="flex items-center justify-between py-2">
                                        <span className="text-sm text-slate-700">{clientPin}</span>

                                        {/* Popup de confirmation pour la suppression */}
                                        <Popup
                                            trigger={
                                                <button
                                                    type="button"
                                                    disabled={isLoading}
                                                    className="text-sm font-medium text-red-600 hover:text-red-800 disabled:text-slate-400"
                                                    title="Supprimer cette liaison"
                                                >
                                                    Supprimer
                                                </button>
                                            }
                                            modal
                                            nested
                                            {...{ overlayStyle: { background: 'rgba(0,0,0,0.5)' } }}
                                        >
                                            {close => (
                                                <div className="mx-auto max-w-sm rounded-2xl bg-white p-5 text-slate-800 shadow-2xl">
                                                    <h3 className="mb-2 text-lg font-semibold text-slate-800">Confirmation</h3>
                                                    <p className="mb-4 text-sm text-slate-600">Êtes-vous sûr de vouloir supprimer la liaison avec {clientPin} ?</p>
                                                    <div className="flex justify-end gap-3">
                                                        <button
                                                            type="button"
                                                            className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                                                            onClick={close}
                                                        >
                                                            Annuler
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                                                            onClick={() => {
                                                                handleRemoveClient(clientPin);
                                                                close();
                                                            }}
                                                        >
                                                            Accepter
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </Popup>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="py-4 text-center text-sm text-slate-500">Aucun client n'est lié à ce serveur.</p>
                        )}
                    </div>
                </div>

                {/* Bouton Fermer */}
                {/* <div className="flex justify-end mt-4">
                    <button
                        onClick={onClose}
                        className="text-slate-700 bg-slate-200 hover:bg-slate-300 focus:ring-4 focus:ring-slate-300 font-medium rounded-lg text-sm px-5 py-2.5"
                    >
                        Fermer
                    </button>

                </div> */}
                <div className="mt-4 flex justify-between">
                        {/* Bouton Fermer à GAUCHE */}
                        <button
                            onClick={onClose}
                            className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                        >
                            Fermer
                        </button>

                        {/* Bouton Gérer à DROITE (Ouvre le sélecteur) */}
                        <button
                            onClick={() => setIsSelectorVisible(true)}
                            disabled={isLoading}
                            className="rounded-lg bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-600 disabled:bg-slate-300"
                        >
                            Gérer
                        </button>
                    </div>
            </div>
        </div> 
        <div className="relative z-50">
                <MultiDeviceSelector
                    isVisible={isSelectorVisible}
                    onClose={() => setIsSelectorVisible(false)}
                    onSubmit={handleAddClients}
                    // On masque le serveur lui-même ET les clients déjà présents dans la liste
                    currentPinCode={serverData.pin_code_serveur}
                    // Si vous avez modifié MultiDeviceSelector pour accepter existingClientPins :
                    // existingClientPins={clients} 
                />
            </div>
        </>

    );
};

export default ModalEditVideowall;